import { combineReducers } from 'redux'
import { Reducer } from 'interfaces'
import { Action } from 'actions'
import { Timeframe } from 'interfaces/chart'
import {
  AccountBreakdownState,
  AccountsRatesState,
  AccountTrendsByCategory,
  AccountTrendsState,
  AccountType,
  AllAccountsDetailedState,
  AssetsDataState,
  DebtsDataState,
} from 'interfaces/accounts'
import { GET_MEMBER_ALL_ACCOUNTS, SET_ACCOUNT_RATES, SET_MEMBER_ACCOUNT_BREAKDOWN, SET_MEMBER_ACCOUNT_TRENDS } from 'actions/accountsActions'
import {
  createAccountBreakdownDiscardTriggers,
  createAccountRatesDiscardTriggers,
  createAccountsDiscardTriggers,
  createAccountTrendDiscardTriggers,
  needDiscardData,
} from 'helpers/discardTriggerHelpers'
import { createTimeframedState } from 'reducers/globalReducer'
import { isSuccessAction } from 'helpers/actionsHelpers'

const createAllAccountsDetailedReducer = (type: AccountType) => {
  const triggers = createAccountsDiscardTriggers(type)

  return (state: AllAccountsDetailedState = null, action: Action): AllAccountsDetailedState => {
    if (needDiscardData(triggers)(action)) {
      return null
    }

    if (action.type === GET_MEMBER_ALL_ACCOUNTS && isSuccessAction(action) && type === action.payload.params.type) {
      return action.payload.response
    }

    return state
  }
}

const createTrendsByCategoryReducer = (type: AccountType) => {
  const triggers = createAccountTrendDiscardTriggers(type)

  return (timeframe: Timeframe): Reducer<AccountTrendsByCategory> =>
    (state = {}, action) => {
      if (needDiscardData(triggers)(action)) {
        return {}
      }

      if (
        action.type === SET_MEMBER_ACCOUNT_TRENDS &&
        action.payload.type === type &&
        action.payload.timeframe === timeframe
      ) {
        return { ...state, [action.payload.category]: action.payload.data }
      }

      return state
    }
}

const createAccountsTrendReducer = (type: AccountType): Reducer<AccountTrendsState> => {
  const creator = createTrendsByCategoryReducer(type)
  return createTimeframedState(creator)
}


const createAccountsRatesReducer = (type: AccountType) => {
  const triggers = createAccountRatesDiscardTriggers(type)

  return (state: AccountsRatesState | null = null, action: Action): AccountsRatesState | null => {
    if (needDiscardData(triggers)(action)) {
      return null
    }

    if (action.type === SET_ACCOUNT_RATES && type === action.payload.type) {
      const { data, timeframe } = action.payload

      return {
        ...state,
        [timeframe]: data,
      }
    }

    return state
  }
}


const createAccountsBreakdownReducer = (type: AccountType) => {
  const triggers = createAccountBreakdownDiscardTriggers(type)

  return (state: AccountBreakdownState = {}, action: Action): AccountBreakdownState => {
    if (needDiscardData(triggers)(action)) {
      return {}
    }

    if (action.type === SET_MEMBER_ACCOUNT_BREAKDOWN && type === action.payload.type) {
      const { data, date } = action.payload
      return {
        ...state,
        [date]: data,
      }
    }

    return state
  }
}

export const assetsData = combineReducers<AssetsDataState>({
  accounts: createAllAccountsDetailedReducer('assets'),
  breakdown: createAccountsBreakdownReducer('assets'),
  rates: createAccountsRatesReducer('assets'),
  trends: createAccountsTrendReducer('assets'),
})

export const debtsData = combineReducers<DebtsDataState>({
  accounts: createAllAccountsDetailedReducer('debts'),
  breakdown: createAccountsBreakdownReducer('debts'),
  rates: createAccountsRatesReducer('debts'),
  trends: createAccountsTrendReducer('debts'),
})
